import { UserType } from "encamp-prisma/generated/browser";

/** Stands in for an Encamp employee's name wherever a customer can see it. */
export const ENCAMP_STAFF_DISPLAY_NAME = "Encamp Staff";

/**
 * Mirrors `STAFF_USER_TYPES` from `encamp-shared/auth`, which cannot be
 * imported here because the auth module pulls in the server-only prisma
 * client and this module must stay browser-safe.
 */
const STAFF_USER_TYPES: ReadonlyArray<string> = [UserType.STAFF_ADMIN];

export type NamedUser = {
  email?: string | null;
  person?: { first?: string | null; last?: string | null } | null;
};

export type UserForStaffDisplay = NamedUser & { type: string };

export function isStaffUserType(type: string): boolean {
  return STAFF_USER_TYPES.includes(type);
}

export function getVisibleWaitingOnUsers<TUser extends UserForStaffDisplay>(
  users: TUser[],
  { viewerIsStaff }: { viewerIsStaff: boolean }
): TUser[] {
  if (viewerIsStaff) {
    return users;
  }
  return users.filter((user) => !isStaffUserType(user.type));
}

/** Full name, falling back to the email address, then to an empty string. */
export function getUserDisplayName(user: NamedUser | null | undefined): string {
  if (!user) {
    return "";
  }
  const name = `${user.person?.first ?? ""} ${user.person?.last ?? ""}`.trim();
  return name || user.email || "";
}

/**
 * Display names for the users a submission is waiting on.
 *
 * Customers must not learn which employee holds their submission, but a blank
 * column while it sits in Encamp review reads as "waiting on nobody", so staff
 * collapse to a single `Encamp Staff` entry rather than being dropped.
 */
export function getWaitingOnDisplayNames(
  users: UserForStaffDisplay[],
  { viewerIsStaff }: { viewerIsStaff: boolean }
): string[] {
  let includesStaffPlaceholder = false;

  return users.flatMap((user) => {
    if (!viewerIsStaff && isStaffUserType(user.type)) {
      if (includesStaffPlaceholder) {
        return [];
      }
      includesStaffPlaceholder = true;
      return [ENCAMP_STAFF_DISPLAY_NAME];
    }

    const displayName = getUserDisplayName(user);
    return displayName ? [displayName] : [];
  });
}
